import React from 'react'
import { useQuery } from '@apollo/client'
import {
  PageSection as Section,
  PageHeader,
  Grid,
  LoadingCard,
  Callout,
  Button,
} from '@superhi/design'
import { useRouter } from 'next/router'

import { TYPE_OPTIONS } from '../../config'

import QUERY from './queries'
import { Props } from './data'
import ROUTE from './route'

import { API } from 'src/api'
import Aside from 'src/components/Aside'
import Main from 'src/components/Main'
import Page from 'src/components/Page'
import NoResults from 'src/components/NoResults'
import StudentWorkCard from 'src/components/StudentWorkCard'
import SEO from 'src/components/SEO'
import Results from 'src/components/Results'
import Data from 'src/components/Data'
import { PageConfig } from 'src/routing/types'

const PER_PAGE_DEFAULT = 30

const LOADING_ITEMS: null[] = new Array(12).fill(null)

type FiltersConfig = {
  type: API.HallOfFamerType | undefined
}

const GRID_1_CELL_SPAN_1: React.ComponentProps<typeof Grid.Cell>['span'] = {
  small: 'full',
  medium: 4,
  large: 4,
}

const HallOfFameHome: PageConfig<Props> = () => {
  const router = useRouter()
  const page = (parseInt(router.query.page as unknown as string) as number) || 1

  const { data, loading } = useQuery<API.HallOfFameHome, API.HallOfFameHomeVariables>(QUERY, {
    variables: {
      type: router.query.type as API.HallOfFamerType,
      page,
      perPage: PER_PAGE_DEFAULT,
    },
    ssr: false,
  })

  return (
    <Page>
      <SEO
        title="See our students' work in the SuperHi Hall of Fame"
        description="Our students make amazing things. Here’s a small selection of the design and code projects they've been working on."
      />

      <Main>
        <PageHeader
          title="Hall of Fame"
          subtitle="We’re really proud of all the impressive work by our course students. Take a look&thinsp;&mdash;&thinsp;it might spark ideas for your own creations."
          backgroundVersion="YELLOW_01"
        />

        <Results<FiltersConfig>
          name="hof-results"
          numResults={data?.hallOfFamers?.totalCount || 0}
          filters={{
            type: TYPE_OPTIONS,
          }}
          query={router.query}
          perPage={PER_PAGE_DEFAULT}
          isLoading={loading}
        >
          <Section>
            <Grid alignItems="stretch">
              <Data
                data={data?.hallOfFamers?.nodes!}
                loading={loading}
                whenLoading={
                  <>
                    {LOADING_ITEMS.map((_, i) => (
                      <Grid.Cell key={i} span={GRID_1_CELL_SPAN_1}>
                        <LoadingCard imageAspectRatio="16:9" />
                      </Grid.Cell>
                    ))}
                  </>
                }
                isEmpty={(data) => data?.length === 0}
                whenEmpty={
                  <Grid.Cell span="full">
                    <NoResults hAlign="center" />
                  </Grid.Cell>
                }
              >
                {(items) => (
                  <>
                    {items.map((item, i) => (
                      <Grid.Cell key={item.slug} span={GRID_1_CELL_SPAN_1}>
                        <StudentWorkCard {...item} imageLazy={i > 2} />
                      </Grid.Cell>
                    ))}
                  </>
                )}
              </Data>
            </Grid>
          </Section>
        </Results>

        <Aside>
          <Callout
            backgroundColor="blue"
            title="Want something you made featured in the Student Hall Of Fame?"
          >
            <Button
              level={Callout.ButtonLevel}
              href="https://superhi.typeform.com/to/CqVmy4ot"
              isExternal
            >
              Submit your work
            </Button>
          </Callout>
        </Aside>
      </Main>
    </Page>
  )
}

HallOfFameHome.route = ROUTE

export { getStaticProps } from './data'
export default HallOfFameHome
